import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Label, Input, FormFeedback } from 'reactstrap';
import { useSelectedVariantContext } from '../context/SelectedVariantContext';
import { Layer, Rect, Stage } from 'react-konva';
import TextLayer from './previewSaveTemplate/TextLayer';
import ImageLayer from './previewSaveTemplate/ImageLayer';
import useUploadFile from '../../../hooks/useUploadFile';
import { toaster } from '../../../shared/PFToast';
import { correctPosition, distance, getRotatedBoundingBox2 } from '../context/SelectedVariantContext/helper';
import { useEditorSides } from '../../../hooks/useEditorSides';
import { useEditorStageSides } from '../../../hooks/useEditorStageSides';
import { actionChangeTabDesign, actionTriggerLoadTemplate } from '../context/SelectedVariantContext/action';

function dataURLtoFile(dataurl, filename) {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
}

const MockupSaveTemplate = ({ modal, toggle, args }) => {
    const {
        state: { editorWrapper, selectedLayer, multiSelectionLayers, originalTransformerRect },
        dispatch,
    } = useSelectedVariantContext()

    const [layers, setLayers] = useState([])
    const [scale, setScale] = useState({ x: 0.2, y: 0.2 })
    const [stageSize, setStageSize] = useState({ width: 352, height: 352 })
    const { uploadTemplate } = useUploadFile()
    const [templateName, setTemplateName] = useState('')
    const [isLoading, setIsLoading] = useState(false)


    const stageRef = useRef(null)

    const [error, setError] = useState(null)

    useEffect(() => {
        const filteredLayers = [selectedLayer, ...(multiSelectionLayers || [])].filter(layer => layer !== null)
        const correctedLayers = filteredLayers.map(layer => {
            return correctPosition(layer)
        })
        const rawMinX = Math.min(...correctedLayers.map(layer => layer.x || 0))
        const rawMinY = Math.min(...correctedLayers.map(layer => layer.y || 0))
        const finalLayers = correctedLayers.map((layer) => {
            if (layer.layerType === 'text') {
                return {
                    ...layer,
                    x: layer.x + layer.offsetX - rawMinX,
                    y: layer.y + layer.offsetY - rawMinY,
                }
            }
            return {
                ...layer,
                x: layer.x + layer.minusParams.x - rawMinX,
                y: layer.y + layer.minusParams.y - rawMinY,
            }
        })
        setLayers(finalLayers)
        if (filteredLayers.length > 1) {
            const scale = Math.min(352 / originalTransformerRect.width, 352 / originalTransformerRect.height)
            setScale({ x: scale, y: scale })
            setStageSize({ width: originalTransformerRect.width * scale, height: originalTransformerRect.height * scale })
        } else {
            const originalRect = getRotatedBoundingBox2({
                width: filteredLayers[0].width,
                height: filteredLayers[0].height,
                rotation: filteredLayers[0].rotation,
                scaleX: filteredLayers[0].scaleX,
                scaleY: filteredLayers[0].scaleY,
            })
            const { width, height } = distance(originalRect);

            const scale = Math.min(352 / width, 352 / height)
            setScale({ x: scale, y: scale })
            setStageSize({ width: width * scale, height: height * scale })
        }
    }, [multiSelectionLayers, selectedLayer]);

    useEffect(() => {
        if (modal) {
            setTemplateName('')
        }
    }, [modal])

    const validateTemplateName = (name) => {
        if (!!name.length && name.trim() === '') {
            setError('Please enter valid text. Only spaces are not allowed.')
            return false
        }
        if (name.trim() === '') {
            setError('Template name is required')
            return false
        }
        return true
    }

    const handleSave = async () => {
        if (!validateTemplateName(templateName)) {
            return
        }
        setIsLoading(true)
        const uri = stageRef.current.toDataURL();
        const file = dataURLtoFile(uri, 'stage.png');
        const saveLayers = [...[selectedLayer, ...(multiSelectionLayers || [])].filter(layer => layer !== null)].map(layer => {
            const finalLayer = layers.find(l => l.id === layer.id)
            return {
                ...layer,
                x: finalLayer ? finalLayer.x : layer.x,
                y: finalLayer ? finalLayer.y : layer.y,
            }
        })
        uploadTemplate(file, { name: templateName, layers: saveLayers, width: stageSize.width / scale.x, height: stageSize.height / scale.y }, (success) => {
            if (success) {
                setIsLoading(false)
                toaster({
                    type: 'success',
                    message: <div>Design has been successfully saved to template. <button className='btn btn-link btn-sm save-template-toast-redirect' onClick={() => {
                        dispatch(actionChangeTabDesign('templates'))
                    }}>Open template</button></div>,
                    width: 500,
                })
                dispatch(actionTriggerLoadTemplate(true))
                toggle()
            } else {
                setIsLoading(false)
            }
        });
    }

    const layerRef2 = useRef(null)

    return (
        <Modal isOpen={modal} toggle={toggle} centered size="md" className='save-template-modal' >
            <ModalHeader toggle={toggle} className='modal-header-save-template'><div className='fs-18 fw-500'>Save as template</div></ModalHeader>
            <ModalBody>
                <div className="export-template-preview">
                    <Stage width={stageSize.width} height={stageSize.height}
                        scaleX={scale.x}
                        scaleY={scale.y}
                        ref={stageRef}>
                        <Layer ref={layerRef2}>
                            {layers.map((layer, i) => {
                                const props = {
                                    key: layer.id,
                                    background: {
                                        width: stageSize.width,
                                        height: stageSize.height,
                                        ratioDefault: scale.x,
                                    }
                                }

                                return layer.layerType === 'text' ? (
                                    <TextLayer {...props} textProps={layer} />
                                ) : (
                                    <>
                                        <ImageLayer {...props} imageProps={layer} />
                                    </>
                                )
                            })}
                        </Layer>
                    </Stage>
                </div>

                <Label for="templateName_modal_input" className="mt-4 fw-500">
                    Template name
                </Label>
                <Input
                    id="templateName_modal_input"
                    className='templateName_modal_input'
                    name="templateName"
                    placeholder="Enter name"
                    type="text"
                    invalid={!!error}
                    maxLength={50}
                    value={templateName}
                    onChange={(e) => {
                        setTemplateName(e.target.value)
                        setError(null)
                    }}
                />
                {error && <FormFeedback>{error}</FormFeedback>}
            </ModalBody>
            <ModalFooter>
                <Button outline onClick={toggle} className='btn-cancel-save-template btn-default'>
                    Cancel
                </Button>
                {' '}
                <Button color="primary" onClick={handleSave} disabled={!templateName || isLoading} className='btn-save-template'>
                    {isLoading ? 'Saving...' : 'Save'}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default MockupSaveTemplate
